import type { Attribution, IntakeApi, Referral } from '../api';
import { REFERRAL_VAR, readCookie } from './attribution';
import { tripDraft, updateTripDraft } from './session';

const REF = /^[A-Za-z0-9_.@-]{1,64}$/;
const DAY_MS = 86_400_000;

/**
 * AffiliateWP's cookies, as its own tracking script sets them (same names, lifetime, path `/`,
 * and its cookie domain when the page is on it). A `set` without a visit id clears the old
 * visit's cookie, so it can't pair with another Buddy.
 */
export function writeAffiliateCookies(
  r: Referral,
  campaign: string | undefined,
  doc: Pick<Document, 'cookie'> = document,
  loc: Pick<Location, 'hostname' | 'protocol'> = window.location,
) {
  if (r.action !== 'set' || !r.affiliateId) return;
  const onDomain =
    r.cookieDomain && (loc.hostname === r.cookieDomain || loc.hostname.endsWith(`.${r.cookieDomain}`));
  const tail = `; path=/${onDomain ? `; domain=${r.cookieDomain}` : ''}; SameSite=Lax${loc.protocol === 'https:' ? '; Secure' : ''}`;
  const keep = `; expires=${new Date(Date.now() + r.cookieDays * DAY_MS).toUTCString()}${tail}`;
  const drop = `; expires=${new Date(0).toUTCString()}${tail}`;
  doc.cookie = `affwp_ref=${r.affiliateId}${keep}`;
  doc.cookie = r.visitId ? `affwp_ref_visit_id=${r.visitId}${keep}` : `affwp_ref_visit_id=${drop}`;
  doc.cookie = campaign ? `affwp_campaign=${encodeURIComponent(campaign)}${keep}` : `affwp_campaign=${drop}`;
}

/**
 * A Buddy link opened straight into the funnel (`/claim/…?buddy=<ref>`; decisions 36 and 56):
 * ask the API which Buddy it names and whether this browser now credits them (AffiliateWP's
 * rule: a cookie it already holds keeps the credit), set AffiliateWP's cookies, and return
 * what changes in the trip draft's attribution (null: nothing).
 *
 * - `set`: the numeric affiliate id, the visit and the campaign replace the raw ref.
 * - `keep`: the cookies `readAttribution` already read stay.
 * - `none`: no active Buddy by that name; the raw ref is dropped.
 * - Any failure (the API or WordPress down): the raw ref stays, and the API resolves it
 *   before filing (without a visit).
 *
 * The static site does the same on its own pages (delay-buddy-new `assets/buddy-ref.js`), and
 * hands over `?buddy=<affiliate id>` with the cookie already set: that case asks nothing.
 */
export async function applyReferral(
  api: Pick<IntakeApi, 'resolveReferral'>,
  search: string,
  href: string,
  referrer: string,
  doc: Pick<Document, 'cookie'> = document,
  loc: Pick<Location, 'hostname' | 'protocol'> = window.location,
): Promise<Partial<Attribution> | null> {
  const qs = new URLSearchParams(search);
  const ref = qs.get(REFERRAL_VAR)?.trim();
  if (!ref || !REF.test(ref)) return null;
  const held = readCookie('affwp_ref', doc.cookie);
  const current = held && /^\d{1,20}$/.test(held) ? held : undefined;
  if (current && current === ref) return null;
  const campaign = qs.get('campaign')?.trim().slice(0, 50) || undefined;

  let r: Referral;
  try {
    r = await api.resolveReferral({
      ref,
      ...(current ? { current } : {}),
      landingUrl: href.slice(0, 1000),
      ...(referrer ? { referrer: referrer.slice(0, 1000) } : {}),
      ...(campaign ? { campaign } : {}),
    });
  } catch {
    return null;
  }
  if (r.action === 'set' && r.affiliateId) {
    writeAffiliateCookies(r, campaign, doc, loc);
    return {
      affwpRef: String(r.affiliateId),
      affwpVisitId: r.visitId ? String(r.visitId) : undefined,
      affwpCampaign: campaign,
    };
  }
  if (r.action === 'none' && !current) return { affwpRef: undefined };
  return null;
}

/** The landing's referral while the API is asked (null once it has answered, or when there is none). */
let pending: Promise<void> | null = null;

/**
 * The landing's `applyReferral`, its answer merged into the trip draft. Nothing waits for it
 * except the enquiry's creation (`referralSettled`).
 */
export function landReferral(
  api: Pick<IntakeApi, 'resolveReferral'>,
  search: string,
  href: string,
  referrer: string,
) {
  const run = applyReferral(api, search, href, referrer)
    .then((changed) => {
      if (changed) updateTripDraft({ attribution: { ...tripDraft.get().attribution, ...changed } });
    })
    .finally(() => {
      if (pending === run) pending = null;
    });
  pending = run;
}

/**
 * Resolves once the landing's referral has answered, or after `maxMs`: the enquiry is created
 * with the Buddy's affiliate id and visit when they arrive in time (a check whose trip is
 * already in the draft creates it straight away), and with the raw `?buddy=` otherwise.
 */
export function referralSettled(maxMs = 3000): Promise<void> {
  const p = pending;
  if (!p) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, maxMs);
    void p.finally(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}
